// The composer tray's state: one tray per composer (conversation or thread), kept across
// conversation switches the way a draft is — switching away and back finds the files still there
// and still uploading. Uploads run in the worker (data/features/attachments.ts); ONE poller here
// asks for every moving upload's status a few times a second and stops when nothing moves.
import { useCallback, useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import { AttachmentKind, type AttachmentKindValue } from '../../../core/attachmentKind';
import { admit, nameForPaste, refusalNotice, safeName, type Candidate, type Refusal, type UploadState } from '../../../core/attachmentStaging';
import type { AttachmentLimits, UploadStatus } from '../../../data/features/attachments';
import { forgetLocal, registerLocal, updateLocal } from './localFiles';

export interface TrayItem {
  localId: string;
  name: string;
  size: number;
  mimeType: string;
  kind: AttachmentKindValue;
  objectUrl: string | null;
  state: UploadState;
  loaded: number;
  total: number;
  fileId: string | null;
  error: string | null;
}

export interface TrayNotice {
  reason: Refusal | 'disabled';
  name: string;
  others: number;
}

export interface Tray {
  channelId: string;
  items: TrayItem[];
  notice: TrayNotice | null;
}

const EMPTY: Tray = { channelId: '', items: [], notice: null };
const trays = new Map<string, Tray>();
const listeners = new Map<string, Set<() => void>>();

function set(key: string, next: Tray): void {
  trays.set(key, next);
  for (const l of listeners.get(key) ?? []) l();
}

function patchItem(key: string, localId: string, patch: Partial<TrayItem>): void {
  const tray = trays.get(key);
  if (!tray || !tray.items.some((i) => i.localId === localId)) return;
  set(key, { ...tray, items: tray.items.map((i) => (i.localId === localId ? { ...i, ...patch } : i)) });
}

export function useTray(key: string): Tray {
  const subscribe = useCallback(
    (l: () => void) => {
      let s = listeners.get(key);
      if (!s) listeners.set(key, (s = new Set()));
      s.add(l);
      return () => void s!.delete(l);
    },
    [key],
  );
  return useSyncExternalStore(subscribe, () => trays.get(key) ?? EMPTY);
}

export function trayOf(key: string): Tray {
  return trays.get(key) ?? EMPTY;
}

// MARK: - Limits (read once per composer mount; the worker answers from sync_state)

let limitsCache: AttachmentLimits | null = null;

export async function loadLimits(): Promise<AttachmentLimits> {
  try {
    limitsCache = await data.call('attachmentLimits');
  } catch {
    /* keep the last answer */
  }
  return limitsCache ?? { maxFiles: 10, maxFileSize: 50 * 1024 * 1024, enabled: true };
}

export function currentLimits(): AttachmentLimits | null {
  return limitsCache;
}

// MARK: - Actions

export interface Incoming {
  file: File;
  /** 'paste' renames clipboard picture data the Mac way ("imagen-XXXXXXXX.png"). */
  source: 'picker' | 'paste' | 'drop';
  isDirectory?: boolean;
}

function newLocalId(): string {
  return 'local-' + crypto.randomUUID().replace(/-/g, '');
}

export async function addFiles(key: string, channelId: string, incoming: Incoming[]): Promise<void> {
  if (!incoming.length) return;
  const limits = await loadLimits();
  const tray = trayOf(key);
  if (!limits.enabled) {
    set(key, { ...tray, channelId, notice: { reason: 'disabled', name: '', others: 0 } });
    return;
  }
  const named = incoming.map((i) => ({
    ...i,
    name: i.source === 'paste' ? nameForPaste(i.file.name, i.file.type, crypto.randomUUID()) : safeName(i.file.name),
  }));
  const candidates: Candidate[] = named.map((i) => ({ name: i.name, size: i.file.size, isDirectory: i.isDirectory }));
  const verdict = admit(tray.items.length, candidates, limits);
  const added = verdict.accepted.map((index) => {
    const { file, name } = named[index];
    const kind = AttachmentKind.classify(file.type, name);
    const objectUrl = kind === 'image' ? URL.createObjectURL(file) : null;
    const localId = newLocalId();
    registerLocal({ localId, name, size: file.size, mimeType: file.type || 'application/octet-stream', objectUrl, width: 0, height: 0, serverId: null, progress: 0 });
    const item: TrayItem = { localId, name, size: file.size, mimeType: file.type, kind, objectUrl, state: 'uploading', loaded: 0, total: file.size, fileId: null, error: null };
    return { item, file };
  });
  const notice = refusalNotice(verdict.refused.map((r) => ({ name: named[r.index].name, reason: r.reason })));
  set(key, { channelId, items: [...tray.items, ...added.map((a) => a.item)], notice: notice ?? (added.length ? null : tray.notice) });
  for (const { item, file } of added) {
    watch(key, item.localId);
    void data.call('attachmentUpload', item.localId, channelId, file, item.name).then(
      (s) => apply(key, s),
      (e) => patchItem(key, item.localId, { state: 'failed', error: String(e?.message ?? e) }),
    );
  }
}

export function removeItem(key: string, localId: string): void {
  const tray = trayOf(key);
  unwatch(localId);
  void data.call('attachmentCancel', localId);
  forgetLocal(localId);
  set(key, { ...tray, items: tray.items.filter((i) => i.localId !== localId) });
}

export function retryItem(key: string, localId: string): void {
  patchItem(key, localId, { state: 'uploading', error: null, loaded: 0 });
  updateLocal(localId, { progress: 0 });
  watch(key, localId);
  void data.call('attachmentRetry', localId).then((s) => apply(key, s));
}

export function dismissNotice(key: string): void {
  const tray = trayOf(key);
  if (tray.notice) set(key, { ...tray, notice: null });
}

/**
 * The send takes the tray: the ids the post must carry (server ids where the upload landed, local
 * ids where it is still running — the worker waits for those), and the tray empties. Uploads keep
 * being watched so the sent message's pictures and progress stay live.
 */
export function takeForSend(key: string): string[] {
  const tray = trayOf(key);
  const ids = tray.items.map((i) => i.fileId ?? i.localId);
  for (const i of tray.items) if (i.state === 'uploading') watchers.set(i.localId, null);
  set(key, { ...tray, items: [], notice: null });
  return ids;
}

/**
 * A file that skips the tray and goes straight into a send (a voice note — ChannelViewModel
 * .sendVoiceNote): starts its upload and answers the local id the send carries; the worker waits
 * for the upload like any queued tray file, and the sent row draws from the reader's own copy.
 */
export function uploadForSend(channelId: string, file: File): string {
  const localId = newLocalId();
  const objectUrl = URL.createObjectURL(file);
  registerLocal({ localId, name: file.name, size: file.size, mimeType: file.type || 'application/octet-stream', objectUrl, width: 0, height: 0, serverId: null, progress: 0 });
  watchers.set(localId, null);
  if (!timer) timer = setTimeout(poll, POLL_MS);
  void data.call('attachmentUpload', localId, channelId, file, file.name).then(
    (s) => apply(null, s),
    () => {
      updateLocal(localId, { progress: -1 });
      watchers.delete(localId);
    },
  );
  return localId;
}

// MARK: - The poller

/** localId → the tray key it belongs to (null once it left the tray with a send). */
const watchers = new Map<string, string | null>();
let timer: ReturnType<typeof setTimeout> | null = null;
const POLL_MS = 250;

function watch(key: string, localId: string): void {
  watchers.set(localId, key);
  if (!timer) timer = setTimeout(poll, POLL_MS);
}

function unwatch(localId: string): void {
  watchers.delete(localId);
}

function apply(key: string | null, s: UploadStatus): void {
  const state: UploadState | null = s.state === 'uploading' ? 'uploading' : s.state === 'done' ? 'done' : s.state === 'failed' ? 'failed' : null;
  updateLocal(s.localId, {
    progress: s.state === 'done' ? 1 : s.state === 'failed' ? -1 : s.total > 0 ? s.loaded / s.total : 0,
    ...(s.fileId ? { serverId: s.fileId } : {}),
    ...(s.width && s.height ? { width: s.width, height: s.height } : {}),
  });
  if (key && state) patchItem(key, s.localId, { state, loaded: s.loaded, total: s.total || 0, fileId: s.fileId, error: s.error });
  if (state !== 'uploading') watchers.delete(s.localId);
}

async function poll(): Promise<void> {
  timer = null;
  const ids = [...watchers.keys()];
  if (!ids.length) return;
  try {
    const statuses = await data.call('attachmentStatus', ids);
    for (const s of statuses) {
      if (!watchers.has(s.localId)) continue;
      apply(watchers.get(s.localId) ?? null, s);
      if (s.state === 'unknown') watchers.delete(s.localId);
    }
  } catch {
    /* the next tick asks again */
  }
  if (watchers.size && !timer) timer = setTimeout(poll, POLL_MS);
}
